// pages/Transcription.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  BreadCrumbsNew,
  NewFooter,
  NewNavbar,
} from "../../components/index.jsx";
import { Upload, Button, Steps, Form, message } from "antd";
import PersonalInfo from "./components/PersonalInfo.jsx";
import PrescriptionUpload from "./components/PrescriptionUpload.jsx";
import MedicationDetails from "./components/MedicationDetails.jsx";
import OrderPreferences from "./components/OrderPreferences.jsx";
import TranscriptionCheckout from "./components/TranscriptionCheckout.jsx";
import ConfirmationStep from "./components/ConfirmationStep.jsx";

const { Step } = Steps;

const Transcription = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [medications, setMedications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [subStep, setSubStep] = useState(1);
  const [orderOption, setOrderOption] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState(null);
  const [paymentSubStep, setPaymentSubStep] = useState(0);
  const [editingAddress, setEditingAddress] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [personalInfo, setPersonalInfo] = useState({
    name: "",
    email: "",
    phone: "",
    dob: "",
    address1: "",
    address2: "",
    city: "",
    zip: "",
  });
  const [shippingAddress, setShippingAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    postcode: "",
  });

  const stepContentRef = useRef(null);
  const stepContainerRef = useRef(null);
  const headerOffsetRef = useRef(0);

  const breadcrumbs = [
    { label: "Home", path: "/" },
    { label: "Transcription" },
  ];

  useEffect(() => {
    const header = document.querySelector(".header-top");
    if (header) {
      headerOffsetRef.current = header.offsetHeight + 20;
    }
  }, []);

  // Scroll to correct position when step changes
  useEffect(() => {
    if (stepContentRef.current) {
      setTimeout(() => {
        const element = stepContentRef.current;
        if (element) {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition =
            elementPosition + window.pageYOffset - headerOffsetRef.current;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 50);
    }
  }, [currentStep, subStep, paymentSubStep]);

  useEffect(() => {
    isNextDisabled();
  }, [currentStep, personalInfo, fileList, medications, deliveryMethod]);


  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      const isImageOrPDF =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "application/pdf";
      if (!isImageOrPDF) {
        message.error("You can only upload JPG/PNG/PDF files!");
        return false;
      }

      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error("File must smaller than 5MB!");
        return false;
      }

      setFileList([...fileList, file]);
      return false;
    },
    fileList,
  };

  const handleNext = () => {
    if (isNextDisabled()) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep(currentStep + 1);
      setIsTransitioning(false);
    }, 300);
  };

  const handlePrev = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep(currentStep - 1);
      setIsTransitioning(false);
    }, 300);
  };

  const onFinish = (values) => {
    setIsLoading(true);
    console.log("Received values:", values);
    // Here you would typically send the data to your backend
    setTimeout(() => {
      setIsLoading(false);
      message.success("Order submitted successfully!");
      setCurrentStep(currentStep + 1);
    }, 1500);
  };

  const steps = [
    {
      title: "Personal Information",
      content: <PersonalInfo personalInfo={personalInfo} setPersonalInfo={setPersonalInfo} setShippingAddress={setShippingAddress} />,
    },
    {
      title: "Prescription Upload",
      content: <PrescriptionUpload uploadProps={uploadProps} />,
    },
    {
      title: "Medications",
      content: (
        <MedicationDetails
          medications={medications}
          setMedications={setMedications}
          setIsTransitioning={setIsTransitioning}
        />
      ),
    },
    {
      title: "Order Instructions",
      content: (
        <OrderPreferences
          form={form}
          subStep={subStep}
          setSubStep={setSubStep}
          setCurrentStep={setCurrentStep}
          orderOption={orderOption}
          setOrderOption={setOrderOption}
          deliveryMethod={deliveryMethod}
          setDeliveryMethod={setDeliveryMethod}
        />
      ),
    },
    {
      title: "Order Review",
      content: (
        <TranscriptionCheckout
          personalInfo={personalInfo}
          fileList={fileList}
          medications={medications}
          shippingAddress={shippingAddress}
          setShippingAddress={setShippingAddress}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          editingAddress={editingAddress}
          setEditingAddress={setEditingAddress}
          deliveryMethod={deliveryMethod}
        />
      ),
    }
  ];

  const isNextDisabled = () => {
    if (currentStep === 0) {
      const {
        name,
        email,
        phone,
        dob,
        address1,
        city,
        zip,
      } = personalInfo;

      // basic required check
      const requiredMissing =
        !name || !email || !dob || !address1 || !city || !zip;

      // simple email format check
      const emailInvalid = email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

      return requiredMissing || emailInvalid;
    }

    if (currentStep === 1) {
      return !(fileList?.length > 0);
    }

    if (currentStep === 2) {
      return !(medications?.length > 0);
    }

    if (currentStep === 3) {
      if(deliveryMethod === null) return true;
    }

    return false;
  };


  return (
    <>
      <main id="content" className="wrapper layout-page">
        <div className="breadcrumnb-img-bg general-bg">
          <div className="container">
            <NewNavbar />
            <BreadCrumbsNew breadcrumbs={breadcrumbs} title={"Transcription"} />
          </div>
        </div>

        <div
          className="container transcribing-container"
          ref={stepContainerRef}
        >
          <div className="transcribing-wrapper">
            <Steps
              current={currentStep}
              direction="vertical"
              className="steps-vertical"
              onChange={(clickedStep) => {
                if (clickedStep <= currentStep) {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentStep(clickedStep);
                    setIsTransitioning(false);
                  }, 300);
                }
              }}
            >
              {steps.map((step, index) => (
                <Step
                  key={index}
                  title={step.title}
                  className={`
                    custom-step
                    ${currentStep === index ? "active-step" : ""}
                    ${index > currentStep ? "disabled-step" : ""}
                  `}
                />
              ))}
            </Steps>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              className="transcribing-form"
            >
              <div
                ref={stepContentRef}
                className={`step-content-wrapper step_${currentStep + 1} ${
                  isTransitioning ? "transitioning" : ""
                }`}
              >
                {steps[currentStep].content}

                {/* {currentStep !== 3 && ( */}
                  <div className="step-actions">
                    {currentStep > 0 && currentStep < steps.length - 1 && (
                      <Button onClick={handlePrev} className="prev-button">
                        Previous
                      </Button>
                    )}
                    {currentStep < steps.length - 1 && (
                      <Button
                        type="primary"
                        onClick={handleNext}
                        className={
                          isNextDisabled()
                            ? "next-button-disabled"
                            : "next-button"
                        }
                        disabled={isNextDisabled()}
                      >
                        Next
                      </Button>
                    )}
                  </div>
                {/* )} */}
              </div>
            </Form>
          </div>
        </div>

        <NewFooter />
      </main>

      {/* Add CSS for smooth transitions */}
      <style jsx>{`
        .step-content-wrapper {
          transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
        }

        .step-content-wrapper.transitioning {
          opacity: 0;
          transform: translateY(10px);
        }

        .transcribing-container {
          scroll-behavior: smooth;
        }
      `}</style>
    </>
  );
};

export default Transcription;
